import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { api, API } from "@/lib/api";
import { logger } from "@/lib/logger";
import { toast } from "sonner";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { PieChart, Pie, Cell, Tooltip, ResponsiveContainer, Legend } from "recharts";
import { PlusCircle, Save, Trash2, Radio, Building2, ImagePlus, X } from "lucide-react";

const COLORS = ["#083CA6", "#00883A", "#F59E0B", "#EF4444"];
const LEGEND_STYLE = { fontSize: "11px" };

const emptyForm = () => ({
  nome: "",
  descricao: "",
  vagas_total: 100,
  vagas_bolsa_familia: 25,
  vagas_pne: 25,
  vagas_idoso: 25,
  vagas_vulnerabilidades: 25,
});

function GaleriaFotos({ emp, onChanged }) {
  const inputRef = useRef(null);
  const [uploading, setUploading] = useState(false);
  const fotos = emp.fotos || [];

  const upload = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", f);
      await api.post(`/empreendimentos/${emp.id}/fotos`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      toast.success("Foto adicionada");
      onChanged?.();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Erro");
    } finally { setUploading(false); e.target.value = ""; }
  };
  const remover = async (foto_id) => {
    if (!window.confirm("Remover esta foto?")) return;
    try {
      await api.delete(`/empreendimentos/${emp.id}/fotos/${foto_id}`);
      toast.success("Foto removida");
      onChanged?.();
    } catch (e) { toast.error(e.response?.data?.detail || "Erro"); }
  };

  return (
    <div className="mt-4 pt-4 border-t">
      <div className="flex items-center justify-between mb-2">
        <span className="text-xs font-semibold text-slate-600">Galeria de fotos ({fotos.length}/12)</span>
        <input ref={inputRef} type="file" accept="image/png,image/jpeg,image/webp" onChange={upload} className="hidden" data-testid={`upload-foto-${emp.id}`} />
        <Button size="sm" variant="outline" onClick={() => inputRef.current?.click()} disabled={uploading || fotos.length >= 12} data-testid={`btn-upload-foto-${emp.id}`}>
          <ImagePlus className="w-4 h-4 mr-1" />{uploading ? "Enviando..." : "Adicionar foto"}
        </Button>
      </div>
      {fotos.length === 0 ? (
        <div className="text-xs text-slate-400 text-center py-4 border-2 border-dashed rounded">
          Nenhuma foto — o empreendimento aparecerá sem galeria na home
        </div>
      ) : (
        <div className="grid grid-cols-3 md:grid-cols-6 gap-2">
          {fotos.map((f) => (
            <div key={f.id} className="relative group aspect-square rounded overflow-hidden border">
              <img src={`${API}/empreendimentos/${emp.id}/fotos/${f.id}`} alt="foto" className="w-full h-full object-cover" />
              <button
                onClick={() => remover(f.id)}
                className="absolute top-1 right-1 bg-red-600 text-white rounded-full w-6 h-6 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity"
                data-testid={`btn-remover-foto-${f.id}`}
                title="Remover"
              ><X className="w-3 h-3" /></button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function EmpreendimentoCard({ emp, stats, onPublicar, onDeletar, onEditar, onChanged }) {
  const data = [
    { name: "Bolsa Família", value: emp.vagas_bolsa_familia, inscritos: stats?.bolsa_familia || 0 },
    { name: "PNE", value: emp.vagas_pne, inscritos: stats?.pne || 0 },
    { name: "Idoso", value: emp.vagas_idoso, inscritos: stats?.idoso || 0 },
    { name: "Vulnerabilidades", value: emp.vagas_vulnerabilidades, inscritos: stats?.vulnerabilidades || 0 },
  ].filter((d) => d.value > 0);

  const soma = emp.vagas_bolsa_familia + emp.vagas_pne + emp.vagas_idoso + emp.vagas_vulnerabilidades;
  const livres = emp.vagas_total - soma;

  return (
    <Card className={emp.publicado ? "border-2 border-[#00883A] shadow-lg" : ""}>
      <CardHeader className="flex-row items-start justify-between space-y-0">
        <div>
          <CardTitle className="text-lg text-[#083CA6] flex items-center gap-2">
            <Building2 className="w-5 h-5" />{emp.nome}
          </CardTitle>
          <div className="flex gap-2 mt-2">
            <Badge className="bg-slate-100 text-slate-800">Total: {emp.vagas_total} vagas</Badge>
            {emp.publicado && <Badge className="bg-green-100 text-green-800">🟢 Publicado</Badge>}
          </div>
        </div>
        <div className="flex gap-2">
          <Button
            size="sm"
            variant={emp.publicado ? "outline" : "default"}
            className={emp.publicado ? "" : "bg-[#00883A] hover:bg-[#006e2e]"}
            onClick={() => onPublicar(emp)}
            data-testid={`btn-publicar-${emp.id}`}
          >
            <Radio className="w-4 h-4 mr-1" />{emp.publicado ? "Despublicar" : "Publicar no Site"}
          </Button>
          <Button size="sm" variant="outline" onClick={() => onEditar(emp)} data-testid={`btn-editar-emp-${emp.id}`}>
            Editar
          </Button>
          <Button size="sm" variant="outline" className="text-red-600 border-red-300" onClick={() => onDeletar(emp)} data-testid={`btn-deletar-emp-${emp.id}`}>
            <Trash2 className="w-4 h-4" />
          </Button>
        </div>
      </CardHeader>
      <CardContent>
        <div className="grid md:grid-cols-2 gap-4">
          <div className="space-y-2 text-sm">
            {data.map((d, i) => (
              <div key={d.name} className="flex items-center justify-between p-2 bg-slate-50 rounded">
                <div className="flex items-center gap-2">
                  <div className="w-3 h-3 rounded-full" style={{ background: COLORS[i] }} />
                  <span className="font-medium">{d.name}</span>
                </div>
                <div className="text-xs text-slate-600">
                  <span className="font-bold text-[#083CA6]">{d.value}</span> vagas ·{" "}
                  <span className="text-slate-500">{d.inscritos} inscritos</span>
                </div>
              </div>
            ))}
            {livres > 0 && (
              <div className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded p-2">
                ⚠️ {livres} vaga(s) não distribuída(s) entre as categorias.
              </div>
            )}
          </div>
          <div className="h-[220px]">
            <ResponsiveContainer width="100%" height="100%">
              <PieChart>
                <Pie data={data} dataKey="value" nameKey="name" cx="50%" cy="50%" outerRadius={70} label>
                  {data.map((d) => <Cell key={d.name} fill={COLORS[data.indexOf(d)]} />)}
                </Pie>
                <Tooltip />
                <Legend wrapperStyle={LEGEND_STYLE} />
              </PieChart>
            </ResponsiveContainer>
          </div>
        </div>
        <GaleriaFotos emp={emp} onChanged={onChanged} />
      </CardContent>
    </Card>
  );
}

function EmpreendimentoForm({ form, setForm, onSalvar, onCancelar, editing }) {
  const soma = Number(form.vagas_bolsa_familia || 0) + Number(form.vagas_pne || 0)
    + Number(form.vagas_idoso || 0) + Number(form.vagas_vulnerabilidades || 0);
  const overflow = soma > Number(form.vagas_total || 0);
  const field = (k, l, type = "number") => (
    <div>
      <Label htmlFor={k} className="text-xs">{l}</Label>
      <Input
        id={k} type={type}
        value={form[k]}
        onChange={(e) => setForm({ ...form, [k]: type === "number" ? Number(e.target.value) : e.target.value })}
        data-testid={`emp-${k}`}
      />
    </div>
  );
  return (
    <Card className="border-2 border-[#083CA6]">
      <CardHeader>
        <CardTitle className="text-base text-[#083CA6]">
          {editing ? "Editar Empreendimento" : "Novo Empreendimento"}
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-3">
        {field("nome", "Nome do Empreendimento", "text")}
        <div>
          <Label className="text-xs">Descrição (opcional)</Label>
          <textarea
            className="w-full border rounded px-3 py-2 text-sm mt-1 min-h-[80px]"
            value={form.descricao || ""}
            onChange={(e) => setForm({ ...form, descricao: e.target.value })}
            placeholder="Ex.: 200 casas populares no bairro Centro, próximas a escolas e transporte..."
            data-testid="emp-descricao"
          />
        </div>
        <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
          {field("vagas_total", "Vagas Totais")}
          {field("vagas_bolsa_familia", "Faixa 1 · Bolsa Família")}
          {field("vagas_pne", "Faixa 1 · PNE")}
          {field("vagas_idoso", "Faixa 1 · Idoso")}
          {field("vagas_vulnerabilidades", "Faixa 1 · Vulnerabilidades")}
        </div>
        <div className={`text-xs p-2 rounded border ${overflow ? "bg-red-50 border-red-200 text-red-800" : "bg-slate-50 border-slate-200 text-slate-700"}`}>
          Soma das categorias: <strong>{soma}</strong> / {form.vagas_total} vagas
          {overflow && " — Excede o total!"}
        </div>
        <div className="flex gap-2">
          <Button onClick={onSalvar} disabled={overflow || !form.nome} className="bg-[#00883A] hover:bg-[#006e2e]" data-testid="btn-salvar-empreendimento">
            <Save className="w-4 h-4 mr-2" />{editing ? "Atualizar" : "Criar"}
          </Button>
          <Button variant="outline" onClick={onCancelar}>Cancelar</Button>
        </div>
      </CardContent>
    </Card>
  );
}

export default function EmpreendimentosPage() {
  const [items, setItems] = useState([]);
  const [stats, setStats] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState(emptyForm());

  const load = useCallback(async () => {
    try {
      const r = await api.get("/empreendimentos");
      setItems(r.data.items);
      setStats(r.data.stats);
    } catch (e) {
      logger.error("Falha ao listar empreendimentos", e);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const startNew = () => {
    setEditingId(null);
    setForm(emptyForm());
    setShowForm(true);
  };

  const startEdit = (emp) => {
    setEditingId(emp.id);
    setForm({
      nome: emp.nome,
      descricao: emp.descricao || "",
      vagas_total: emp.vagas_total,
      vagas_bolsa_familia: emp.vagas_bolsa_familia,
      vagas_pne: emp.vagas_pne,
      vagas_idoso: emp.vagas_idoso,
      vagas_vulnerabilidades: emp.vagas_vulnerabilidades,
    });
    setShowForm(true);
  };

  const salvar = async () => {
    try {
      if (editingId) {
        await api.put(`/empreendimentos/${editingId}`, form);
        toast.success("Empreendimento atualizado");
      } else {
        await api.post("/empreendimentos", form);
        toast.success("Empreendimento criado");
      }
      setShowForm(false);
      setEditingId(null);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erro ao salvar");
    }
  };

  const publicar = async (emp) => {
    try {
      await api.post(`/empreendimentos/${emp.id}/publicar`, { publicado: !emp.publicado });
      toast.success(emp.publicado ? "Empreendimento despublicado" : "Empreendimento publicado no site");
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erro");
    }
  };

  const deletar = async (emp) => {
    if (!window.confirm(`Excluir "${emp.nome}"?`)) return;
    try {
      await api.delete(`/empreendimentos/${emp.id}`);
      toast.success("Empreendimento excluído");
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erro");
    }
  };

  const totalStatsCards = useMemo(() => stats ? [
    { label: "Inscritos Totais", value: stats.total_inscritos, color: "text-[#083CA6]" },
    { label: "Aptas Faixa 1", value: stats.aptas_faixa1, color: "text-[#00883A]" },
    { label: "Bolsa Família", value: stats.bolsa_familia, color: "text-amber-600" },
    { label: "PNE / Idoso / Vulner.", value: stats.pne + stats.idoso + stats.vulnerabilidades, color: "text-red-600" },
  ] : [], [stats]);

  return (
    <div className="space-y-6" data-testid="empreendimentos-page">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-3xl font-black text-[#083CA6]">Configuração da Página</h1>
          <p className="text-sm text-slate-600">Empreendimentos e distribuição de vagas</p>
        </div>
        {!showForm && (
          <Button onClick={startNew} className="bg-[#083CA6] hover:bg-[#062f85]" data-testid="btn-novo-empreendimento">
            <PlusCircle className="w-4 h-4 mr-2" />Criar Novo Empreendimento
          </Button>
        )}
      </div>

      {stats && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          {totalStatsCards.map((c) => (
            <Card key={c.label}>
              <CardContent className="p-4">
                <div className="text-xs text-slate-500 uppercase tracking-wider">{c.label}</div>
                <div className={`text-2xl font-black ${c.color} mt-1`}>{c.value}</div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {showForm && (
        <EmpreendimentoForm
          form={form}
          setForm={setForm}
          onSalvar={salvar}
          onCancelar={() => { setShowForm(false); setEditingId(null); }}
          editing={!!editingId}
        />
      )}

      {items.length === 0 ? (
        <Card>
          <CardContent className="p-8 text-center text-slate-500">
            Nenhum empreendimento cadastrado.
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-4">
          {items.map((emp) => (
            <EmpreendimentoCard
              key={emp.id}
              emp={emp}
              stats={stats}
              onPublicar={publicar}
              onDeletar={deletar}
              onEditar={startEdit}
              onChanged={load}
            />
          ))}
        </div>
      )}
    </div>
  );
}
